import type { NamespaceCatalog } from '@/i18n/ns';

const en = {
  title: 'Recent operations',
  time: 'Time',
  operation: 'Operation',
  pending: 'Pending',
  note: 'Recorded usage, not a price estimate. Failed work can consume credits. Use person limits to control spending.',
  more: 'Showing the latest 100 operations in this date range.',
  lowBalance: 'Company credits are running low: {credits} left.',
  model: 'AI',
  tool: 'Tool',
  connection: 'Connection',
  speech: 'Audio',
  storage: 'Storage',
  other: 'Other',
};
export type UsageOperationsKey = keyof typeof en;
const copy: NamespaceCatalog<UsageOperationsKey> = {
  en,
  sv: {
    title: 'Senaste åtgärder',
    time: 'Tid',
    operation: 'Åtgärd',
    pending: 'Väntar',
    note: 'Registrerad användning, ingen prisuppskattning. Misslyckat arbete kan förbruka krediter. Styr förbrukningen med personliga gränser.',
    more: 'Visar de senaste 100 åtgärderna i datumintervallet.',
    lowBalance: 'Företagets krediter börjar ta slut: {credits} kvar.',
    model: 'AI',
    tool: 'Verktyg',
    connection: 'Anslutning',
    speech: 'Ljud',
    storage: 'Lagring',
    other: 'Övrigt',
  },
  da: {
    title: 'Seneste handlinger',
    time: 'Tid',
    operation: 'Handling',
    pending: 'Afventer',
    note: 'Registreret forbrug, ikke et prisoverslag. Mislykket arbejde kan bruge kreditter. Brug persongrænser til at styre forbruget.',
    more: 'Viser de seneste 100 handlinger i datointervallet.',
    lowBalance: 'Virksomhedens kreditter er ved at løbe ud: {credits} tilbage.',
    model: 'AI',
    tool: 'Værktøj',
    connection: 'Forbindelse',
    speech: 'Lyd',
    storage: 'Lagring',
    other: 'Andet',
  },
  de: {
    title: 'Letzte Vorgänge',
    time: 'Zeit',
    operation: 'Vorgang',
    pending: 'Ausstehend',
    note: 'Erfasster Verbrauch, keine Preisschätzung. Fehlgeschlagene Arbeit kann Credits verbrauchen. Begrenzen Sie den Verbrauch pro Person.',
    more: 'Die letzten 100 Vorgänge in diesem Zeitraum.',
    lowBalance: 'Die Unternehmenscredits werden knapp: {credits} übrig.',
    model: 'KI',
    tool: 'Werkzeug',
    connection: 'Verbindung',
    speech: 'Audio',
    storage: 'Speicher',
    other: 'Sonstiges',
  },
  es: {
    title: 'Operaciones recientes',
    time: 'Hora',
    operation: 'Operación',
    pending: 'Pendiente',
    note: 'Consumo registrado, no una estimación de precio. El trabajo fallido puede consumir créditos. Use límites por persona para controlar el consumo.',
    more: 'Se muestran las últimas 100 operaciones del intervalo.',
    lowBalance: 'Quedan pocos créditos de la empresa: {credits}.',
    model: 'IA',
    tool: 'Herramienta',
    connection: 'Conexión',
    speech: 'Audio',
    storage: 'Almacenamiento',
    other: 'Otro',
  },
  fr: {
    title: 'Opérations récentes',
    time: 'Heure',
    operation: 'Opération',
    pending: 'En attente',
    note: 'Consommation enregistrée, pas une estimation de prix. Un travail échoué peut consommer des crédits. Fixez des limites par personne.',
    more: 'Les 100 dernières opérations de cette période.',
    lowBalance: 'Les crédits de l’entreprise sont bas : {credits} restants.',
    model: 'IA',
    tool: 'Outil',
    connection: 'Connexion',
    speech: 'Audio',
    storage: 'Stockage',
    other: 'Autre',
  },
  it: {
    title: 'Operazioni recenti',
    time: 'Ora',
    operation: 'Operazione',
    pending: 'In attesa',
    note: 'Consumo registrato, non una stima del prezzo. Anche il lavoro non riuscito può consumare crediti. Imposti limiti per persona.',
    more: 'Le ultime 100 operazioni in questo intervallo.',
    lowBalance: 'I crediti aziendali stanno finendo: ne restano {credits}.',
    model: 'IA',
    tool: 'Strumento',
    connection: 'Connessione',
    speech: 'Audio',
    storage: 'Archiviazione',
    other: 'Altro',
  },
  pt: {
    title: 'Operações recentes',
    time: 'Hora',
    operation: 'Operação',
    pending: 'Pendente',
    note: 'Consumo registado, não uma estimativa de preço. O trabalho falhado pode consumir créditos. Defina limites por pessoa.',
    more: 'As últimas 100 operações neste intervalo.',
    lowBalance: 'Os créditos da empresa estão a acabar: restam {credits}.',
    model: 'IA',
    tool: 'Ferramenta',
    connection: 'Ligação',
    speech: 'Áudio',
    storage: 'Armazenamento',
    other: 'Outro',
  },
  nl: {
    title: 'Recente bewerkingen',
    time: 'Tijd',
    operation: 'Bewerking',
    pending: 'In afwachting',
    note: 'Geregistreerd verbruik, geen prijsraming. Mislukt werk kan credits verbruiken. Stel limieten per persoon in.',
    more: 'De laatste 100 bewerkingen in deze periode.',
    lowBalance: 'De bedrijfscredits raken op: nog {credits}.',
    model: 'AI',
    tool: 'Hulpmiddel',
    connection: 'Verbinding',
    speech: 'Audio',
    storage: 'Opslag',
    other: 'Overig',
  },
  fi: {
    title: 'Viimeisimmät toiminnot',
    time: 'Aika',
    operation: 'Toiminto',
    pending: 'Odottaa',
    note: 'Kirjattu käyttö, ei hinta-arvio. Epäonnistunut työ voi kuluttaa krediittejä. Hallitse kulutusta henkilökohtaisilla rajoilla.',
    more: 'Aikavälin 100 viimeisintä toimintoa.',
    lowBalance: 'Yrityksen krediitit ovat vähissä: jäljellä {credits}.',
    model: 'Tekoäly',
    tool: 'Työkalu',
    connection: 'Yhteys',
    speech: 'Ääni',
    storage: 'Tallennus',
    other: 'Muu',
  },
  bg: {
    title: 'Последни операции',
    time: 'Час',
    operation: 'Операция',
    pending: 'Изчаква',
    note: 'Отчетено потребление, не ценова оценка. Неуспешната работа може да изразходва кредити. Задайте лимити за всеки човек.',
    more: 'Последните 100 операции за периода.',
    lowBalance: 'Кредитите на компанията намаляват: остават {credits}.',
    model: 'ИИ',
    tool: 'Инструмент',
    connection: 'Връзка',
    speech: 'Аудио',
    storage: 'Съхранение',
    other: 'Друго',
  },
  hr: {
    title: 'Nedavne operacije',
    time: 'Vrijeme',
    operation: 'Operacija',
    pending: 'Na čekanju',
    note: 'Zabilježena potrošnja, ne procjena cijene. Neuspješan rad može trošiti kredite. Postavite ograničenja po osobi.',
    more: 'Posljednjih 100 operacija u ovom razdoblju.',
    lowBalance: 'Krediti tvrtke pri kraju su: preostalo {credits}.',
    model: 'UI',
    tool: 'Alat',
    connection: 'Veza',
    speech: 'Zvuk',
    storage: 'Pohrana',
    other: 'Ostalo',
  },
  cs: {
    title: 'Nedávné operace',
    time: 'Čas',
    operation: 'Operace',
    pending: 'Čeká',
    note: 'Zaznamenaná spotřeba, nikoli odhad ceny. Neúspěšná práce může spotřebovat kredity. Nastavte limity pro jednotlivé osoby.',
    more: 'Posledních 100 operací v tomto období.',
    lowBalance: 'Firemní kredity docházejí: zbývá {credits}.',
    model: 'AI',
    tool: 'Nástroj',
    connection: 'Připojení',
    speech: 'Zvuk',
    storage: 'Úložiště',
    other: 'Ostatní',
  },
  et: {
    title: 'Viimased toimingud',
    time: 'Aeg',
    operation: 'Toiming',
    pending: 'Ootel',
    note: 'Registreeritud kasutus, mitte hinnanguline hind. Ebaõnnestunud töö võib kulutada krediiti. Määrake inimestele piirangud.',
    more: 'Selle ajavahemiku viimased 100 toimingut.',
    lowBalance: 'Ettevõtte krediit hakkab lõppema: alles {credits}.',
    model: 'Tehisintellekt',
    tool: 'Tööriist',
    connection: 'Ühendus',
    speech: 'Heli',
    storage: 'Salvestus',
    other: 'Muu',
  },
  el: {
    title: 'Πρόσφατες λειτουργίες',
    time: 'Ώρα',
    operation: 'Λειτουργία',
    pending: 'Σε εκκρεμότητα',
    note: 'Καταγεγραμμένη χρήση, όχι εκτίμηση τιμής. Η αποτυχημένη εργασία μπορεί να καταναλώσει μονάδες. Ορίστε όρια ανά άτομο.',
    more: 'Οι τελευταίες 100 λειτουργίες σε αυτή την περίοδο.',
    lowBalance: 'Οι μονάδες της εταιρείας τελειώνουν: απομένουν {credits}.',
    model: 'ΤΝ',
    tool: 'Εργαλείο',
    connection: 'Σύνδεση',
    speech: 'Ήχος',
    storage: 'Αποθήκευση',
    other: 'Άλλο',
  },
  hu: {
    title: 'Legutóbbi műveletek',
    time: 'Idő',
    operation: 'Művelet',
    pending: 'Függőben',
    note: 'Rögzített fogyasztás, nem árbecslés. A sikertelen munka is fogyaszthat kreditet. Állítson be személyenkénti korlátokat.',
    more: 'Az időszak legutóbbi 100 művelete.',
    lowBalance: 'A vállalat kreditjei fogynak: {credits} maradt.',
    model: 'MI',
    tool: 'Eszköz',
    connection: 'Kapcsolat',
    speech: 'Hang',
    storage: 'Tárhely',
    other: 'Egyéb',
  },
  ga: {
    title: 'Oibríochtaí le déanaí',
    time: 'Am',
    operation: 'Oibríocht',
    pending: 'Ar feitheamh',
    note: 'Úsáid thaifeadta, ní meastachán praghais. Féadfaidh obair theipthe creidmheasanna a úsáid. Socraigh teorainneacha do gach duine.',
    more: 'Na 100 oibríocht is déanaí sa tréimhse seo.',
    lowBalance: 'Tá creidmheasanna na cuideachta íseal: {credits} fágtha.',
    model: 'IS',
    tool: 'Uirlis',
    connection: 'Ceangal',
    speech: 'Fuaim',
    storage: 'Stóráil',
    other: 'Eile',
  },
  lv: {
    title: 'Nesenās darbības',
    time: 'Laiks',
    operation: 'Darbība',
    pending: 'Gaida',
    note: 'Reģistrēts patēriņš, nevis cenas aplēse. Neveiksmīgs darbs var patērēt kredītus. Iestatiet ierobežojumus katrai personai.',
    more: 'Pēdējās 100 darbības šajā periodā.',
    lowBalance: 'Uzņēmuma kredīti izsīkst: atlikuši {credits}.',
    model: 'MI',
    tool: 'Rīks',
    connection: 'Savienojums',
    speech: 'Audio',
    storage: 'Krātuve',
    other: 'Cits',
  },
  lt: {
    title: 'Naujausios operacijos',
    time: 'Laikas',
    operation: 'Operacija',
    pending: 'Laukiama',
    note: 'Užregistruotas naudojimas, ne kainos įvertis. Nesėkmingas darbas gali naudoti kreditus. Nustatykite ribas kiekvienam asmeniui.',
    more: 'Paskutinės 100 operacijų šiuo laikotarpiu.',
    lowBalance: 'Įmonės kreditai senka: liko {credits}.',
    model: 'DI',
    tool: 'Įrankis',
    connection: 'Ryšys',
    speech: 'Garsas',
    storage: 'Saugykla',
    other: 'Kita',
  },
  mt: {
    title: 'Operazzjonijiet reċenti',
    time: 'Ħin',
    operation: 'Operazzjoni',
    pending: 'Pendenti',
    note: 'Użu rreġistrat, mhux stima tal-prezz. Xogħol li ma jirnexxix jista’ juża krediti. Issettja limiti għal kull persuna.',
    more: 'L-aħħar 100 operazzjoni f’dan il-perjodu.',
    lowBalance: 'Il-krediti tal-kumpanija qed jonqsu: fadal {credits}.',
    model: 'IA',
    tool: 'Għodda',
    connection: 'Konnessjoni',
    speech: 'Awdjo',
    storage: 'Ħażna',
    other: 'Oħra',
  },
  pl: {
    title: 'Ostatnie operacje',
    time: 'Czas',
    operation: 'Operacja',
    pending: 'Oczekuje',
    note: 'Zarejestrowane zużycie, nie wycena. Nieudana praca może zużywać kredyty. Ustaw limity dla poszczególnych osób.',
    more: 'Ostatnie 100 operacji w tym okresie.',
    lowBalance: 'Kredyty firmy się kończą: pozostało {credits}.',
    model: 'AI',
    tool: 'Narzędzie',
    connection: 'Połączenie',
    speech: 'Dźwięk',
    storage: 'Pamięć',
    other: 'Inne',
  },
  ro: {
    title: 'Operațiuni recente',
    time: 'Ora',
    operation: 'Operațiune',
    pending: 'În așteptare',
    note: 'Consum înregistrat, nu estimare de preț. Munca nereușită poate consuma credite. Stabiliți limite pentru fiecare persoană.',
    more: 'Ultimele 100 de operațiuni din această perioadă.',
    lowBalance: 'Creditele companiei sunt pe terminate: au rămas {credits}.',
    model: 'IA',
    tool: 'Instrument',
    connection: 'Conexiune',
    speech: 'Audio',
    storage: 'Stocare',
    other: 'Altele',
  },
  sk: {
    title: 'Nedávne operácie',
    time: 'Čas',
    operation: 'Operácia',
    pending: 'Čaká',
    note: 'Zaznamenaná spotreba, nie odhad ceny. Neúspešná práca môže spotrebovať kredity. Nastavte limity pre jednotlivé osoby.',
    more: 'Posledných 100 operácií v tomto období.',
    lowBalance: 'Firemné kredity sa míňajú: zostáva {credits}.',
    model: 'AI',
    tool: 'Nástroj',
    connection: 'Pripojenie',
    speech: 'Zvuk',
    storage: 'Úložisko',
    other: 'Ostatné',
  },
  sl: {
    title: 'Nedavne operacije',
    time: 'Čas',
    operation: 'Operacija',
    pending: 'Na čakanju',
    note: 'Zabeležena poraba, ne ocena cene. Neuspešno delo lahko porablja kredite. Nastavite omejitve za posameznike.',
    more: 'Zadnjih 100 operacij v tem obdobju.',
    lowBalance: 'Krediti podjetja se iztekajo: ostalo jih je {credits}.',
    model: 'UI',
    tool: 'Orodje',
    connection: 'Povezava',
    speech: 'Zvok',
    storage: 'Shramba',
    other: 'Drugo',
  },
};
export default copy;
